import { createAction } from '@reduxjs/toolkit';

/**
 * Channel tag for routing WS-send actions to the ES connection. Set on a
 * dispatched action's `meta.ws` so the app's WS request middleware forwards the
 * payload through the ES channel.
 */
export const wsEsChannel = 'ES' as const;

export interface WsActionMeta extends Record<string, unknown> {}

/**
 * Creates a Redux action tagged for the ES WS channel. The dashboard's WS
 * request middleware reads `meta.ws` and forwards the payload over the ES
 * connection. Reusable across data packages that send ES commands.
 */
export function createEsWsAction(
  type: string,
  callback: () => { command: string },
): ReturnType<
  typeof createAction<
    (meta?: WsActionMeta) => { payload: ReturnType<typeof callback>; meta: WsActionMeta },
    string
  >
>;

export function createEsWsAction<T, P>(
  type: string,
  callback: (data: T) => { command: string } & P,
): ReturnType<
  typeof createAction<
    (data: T, meta?: WsActionMeta) => { payload: ReturnType<typeof callback>; meta: WsActionMeta },
    string
  >
>;

export function createEsWsAction<T>(
  type: string,
  command: string,
): ReturnType<
  typeof createAction<
    (data: T, meta?: WsActionMeta) => { payload: T & { command: string }; meta: WsActionMeta },
    string
  >
>;

export function createEsWsAction<T = undefined, P = undefined>(
  type: string,
  callback: string | (() => { command: string }) | ((data?: T) => { command: string } & P),
) {
  return createAction(type, (data?: T, meta?: WsActionMeta) => ({
    payload:
      typeof callback === 'function'
        ? callback(data)
        : {
            command: callback,
            ...(data && { ...data }),
          },
    meta: {
      ...meta,
      ws: wsEsChannel,
    },
  }));
}
